import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';

import {
  getActiveCycleVersionId,
  getBadgeGating,
  getBadgeStatus,
  getCycleScopedStatus,
  type ReviewBadgeDerivation,
  type ReviewBadgeGatingInput,
  type VersionScopedPanelContext,
  type VersionScopedReviewChange,
} from './reviewBadgeShared';
import {ReviewBadgeStatus} from './ReviewConstants';

/**
 * Cyclical review badge helpers keyed on reviewItemVersionApiIdentifier, which scopes each
 * change to a version so older versions show their own cycle rather than the current one.
 * Linear reviewables use linearReviewBadgeDisplayState instead.
 */

export interface CyclicalVersionBadgeResult {
  kind: 'hidden';
}

export interface CyclicalVersionBadgeStatusResult {
  kind: 'status';
  status: ReviewBadgeStatus;
  scopedStatus: ReviewStatus | null;
}

export type CyclicalVersionBadgeDerivation = CyclicalVersionBadgeResult | CyclicalVersionBadgeStatusResult;

const HIDDEN_REVIEW_BADGE_DERIVATION = {
  isHidden: true,
  badgeStatus: ReviewBadgeStatus.IN_PROGRESS,
  reviewStatus: undefined,
  showSendForReview: false,
  showSummaryPanel: false,
} satisfies ReviewBadgeDerivation;

/** Scopes review summary panel data to the open version for cyclical reviewables. */
export function deriveCyclicalPanelContext<TChange extends VersionScopedReviewChange>({
  openVersionId,
  globalStatus,
  reviewChanges,
}: {
  openVersionId: string;
  globalStatus: ReviewStatus;
  reviewChanges: ReadonlyArray<TChange>;
}): VersionScopedPanelContext<TChange> {
  const scopedReviewChanges = reviewChanges.filter(
    (change) => change.reviewItemVersionApiIdentifier === openVersionId
  );
  const scopedStatus = getCycleScopedStatus(reviewChanges, openVersionId);
  const activeCycleVersionId = getActiveCycleVersionId(reviewChanges);
  const isActiveCycleVersion = activeCycleVersionId != null && openVersionId === activeCycleVersionId;

  return {
    scopedReviewChanges,
    scopedStatus,
    isActiveCycleVersion,
    panelStatus: scopedStatus ?? globalStatus,
  };
}

function toStatusBadge(scopedStatus: ReviewStatus): CyclicalVersionBadgeStatusResult {
  return {kind: 'status', status: getBadgeStatus(scopedStatus), scopedStatus};
}

/**
 * Derives the cyclical review badge for the open template version.
 *
 * - Published versions only show an approved cycle, so a version published without review gating (no
 *   review processes on the collection) never shows its stale rejected or retracted status. Approval
 *   statuses before CYCLE_COMPLETED count too, because review data cached before the publish still ends
 *   with the approval until it is refetched.
 * - Drafts, editable or historical, only show a badge while the collection requires review.
 * - Historical drafts are frozen and can never progress, so a cycle that maps to "In progress" is hidden.
 *
 * @param reviewChanges Time-ordered array of all review changes on the review (ascending by createdAt).
 */
function getCyclicalVersionBadge({
  openVersionId,
  isCurrentEditableDraft,
  isViewingPublishedVersion,
  parentReviewLifecycleCount,
  reviewChanges,
}: {
  openVersionId: string;
  isCurrentEditableDraft: boolean;
  isViewingPublishedVersion: boolean;
  parentReviewLifecycleCount: number;
  reviewChanges: ReadonlyArray<VersionScopedReviewChange>;
}): CyclicalVersionBadgeDerivation {
  const scopedStatus = getCycleScopedStatus(reviewChanges, openVersionId);

  if (isViewingPublishedVersion) {
    return scopedStatus != null && getBadgeStatus(scopedStatus) === ReviewBadgeStatus.ACCEPTED
      ? toStatusBadge(scopedStatus)
      : {kind: 'hidden'};
  }

  if (parentReviewLifecycleCount === 0) {
    return {kind: 'hidden'};
  }

  if (isCurrentEditableDraft) {
    return scopedStatus != null
      ? toStatusBadge(scopedStatus)
      : {kind: 'status', status: ReviewBadgeStatus.IN_PROGRESS, scopedStatus: null};
  }

  if (scopedStatus == null || getBadgeStatus(scopedStatus) === ReviewBadgeStatus.IN_PROGRESS) {
    return {kind: 'hidden'};
  }
  return toStatusBadge(scopedStatus);
}

/** Assembles cyclical review badge status, review status, and dropdown gating for the controller. */
export function deriveCyclicalReviewBadge(
  input: ReviewBadgeGatingInput & {
    openVersionId: string;
    isCurrentEditableDraft: boolean;
    isViewingPublishedVersion: boolean;
    reviewChanges: ReadonlyArray<VersionScopedReviewChange>;
  }
): ReviewBadgeDerivation {
  const versionBadge = getCyclicalVersionBadge({
    openVersionId: input.openVersionId,
    isCurrentEditableDraft: input.isCurrentEditableDraft,
    isViewingPublishedVersion: input.isViewingPublishedVersion,
    parentReviewLifecycleCount: input.parentReviewLifecycleCount,
    reviewChanges: input.reviewChanges,
  });
  if (versionBadge.kind === 'hidden') {
    return HIDDEN_REVIEW_BADGE_DERIVATION;
  }
  return {
    isHidden: false,
    badgeStatus: versionBadge.status,
    reviewStatus: versionBadge.scopedStatus ?? undefined,
    ...getCyclicalBadgeGating({
      enableReviewLifecycle: input.enableReviewLifecycle,
      canSendForReview: input.canSendForReview,
      parentReviewLifecycleCount: input.parentReviewLifecycleCount,
      reviewUsesReviewLifecycle: input.reviewUsesReviewLifecycle,
      scopedStatus: versionBadge.scopedStatus,
      isCurrentEditableDraft: input.isCurrentEditableDraft,
    }),
  };
}

/**
 * Badge gating for cyclical reviewables. Uses scopedStatus (not the lagging global review.status scalar)
 * so a draft with no cycle yet can offer send-for-review while review.status sits at CYCLE_COMPLETED.
 */
function getCyclicalBadgeGating({
  enableReviewLifecycle,
  canSendForReview,
  parentReviewLifecycleCount,
  reviewUsesReviewLifecycle,
  scopedStatus,
  isCurrentEditableDraft,
}: ReviewBadgeGatingInput & {
  scopedStatus: ReviewStatus | null;
  isCurrentEditableDraft: boolean;
}): {showSendForReview: boolean; showSummaryPanel: boolean} {
  if (!isCurrentEditableDraft) {
    return {
      showSendForReview: false,
      showSummaryPanel: reviewUsesReviewLifecycle || scopedStatus != null,
    };
  }
  return getBadgeGating({
    enableReviewLifecycle,
    canSendForReview,
    parentReviewLifecycleCount,
    reviewStatus: scopedStatus,
    reviewUsesReviewLifecycle,
  });
}

export const _forTesting = {
  getCyclicalVersionBadge,
  getCyclicalBadgeGating,
  HIDDEN_REVIEW_BADGE_DERIVATION,
};
